/**
 * The "what is this attached to" rows, shared by the task and the work
 * report detail pages (D4c1).
 *
 * Extracted VERBATIM from ``WorkReportDetailCards`` — same markup, same
 * classes, same behaviour — because the task page needed exactly this
 * and was instead printing raw UUIDs. Only the card chrome differs
 * between the two pages (``DetailCard`` vs ``Section``), so that stays
 * with each page and only the rows are shared.
 *
 * Takes ``{key, label, value, to}`` entries from
 * ``entityLinkEntries.js``. Rendering rules:
 *
 * * ``value`` null → "Nicht verknüpft". Every possible link gets a row,
 *   so the section answers the question completely and a missing row
 *   reads as a bug rather than as an absent link.
 * * ``to`` set → a real ``<Link>`` with a chevron.
 * * ``to`` null → a plain ``<span>``. A chevron is a promise, and a row
 *   with no destination must not make it — which is why an entity
 *   without a detail route renders as text rather than as a link to
 *   nowhere. Tenancies are the remaining case: they live as cards on
 *   the tenant page. Rooms were one too until D4c2 built their page.
 *
 * Icons are decorative (`aria-hidden`); the label beside them carries
 * the meaning, so nothing depends on recognising a glyph.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Building,
  Building2,
  ChevronRight,
  ClipboardCheck,
  DoorOpen,
  FileText,
  Home,
  User,
  UserCheck,
} from "lucide-react";

export const LINK_ICONS = {
  task: ClipboardCheck,
  tenant: User,
  tenancy: FileText,
  unit: Home,
  application: UserCheck,
  landlord: Building2,
  // D4c1
  property: Building,
  room: DoorOpen,
};

/** One link row: label, icon, then the value as an amber pill. */
export function EntityLinkRow({ entry }) {
  const Icon = LINK_ICONS[entry.key] || FileText;
  const missing = !entry.value;

  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-b-0 dark:border-[#1e2130]/70">
      <span className="flex shrink-0 items-center gap-2 text-[12px] text-muted-foreground dark:text-[#4a5070]">
        {entry.label}
      </span>
      <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
        <Icon
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground dark:text-[#4a5070]"
          aria-hidden="true"
        />
        {missing ? (
          <span className="text-[12px] text-muted-foreground dark:text-[#4a5070]">
            Nicht verknüpft
          </span>
        ) : entry.to ? (
          <Link
            to={entry.to}
            className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-[8px] border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[12px] font-medium text-amber-700 transition-colors hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-400"
          >
            <span className="truncate">{entry.value}</span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          </Link>
        ) : (
          <span className="inline-flex min-w-0 max-w-full rounded-[8px] border border-border bg-muted/60 px-2.5 py-1 text-[12px] font-medium text-foreground dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0]">
            <span className="truncate">{entry.value}</span>
          </span>
        )}
      </span>
    </div>
  );
}

/** The whole list of rows, without any card chrome. */
export default function EntityLinkRows({ entries }) {
  return (
    <div>
      {(entries || []).map((entry) => (
        <EntityLinkRow key={entry.key} entry={entry} />
      ))}
    </div>
  );
}
